// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import setReorderPadView from "@keller/App/Handlers/ReorderPad/SetReorderPadView";
import { ReorderPadProductListViewType } from "@keller/App/Store/Reducers";
import { getReorderPad } from "@keller/App/Store/Selectors/ReorderPadSelectors";
import GridView from "@keller/XDL/Icons/GridView";
import TableView from "@keller/XDL/Icons/TableView";
import { getSystemColor, xScreen } from "@keller/XDL/Utilities";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {}

const mapStateToProps = (state: ApplicationState) => ({
    loaded: !!getReorderPad(state).views,
    view: getReorderPad(state).view,
});

const mapDispatchToProps = {
    setReorderPadView,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListViewSelectStyles {
    wrapper?: InjectableCss;
    gridViewClickable?: ClickablePresentationProps;
    gridViewIcon?: IconPresentationProps;
    tableViewClickable?: ClickablePresentationProps;
    tableViewIcon?: IconPresentationProps;
    title?: TypographyPresentationProps;
}

export const viewSelectStyles: ProductListViewSelectStyles = {
    title: {
        size: 15,
        css: css`
            letter-spacing: 0;
            line-height: 24px;
            margin-right: 16px;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            float: right;
            align-items: center;
            ${xScreen("lte", "sm")} {
                display: none;
            }
        `,
    },
    gridViewClickable: {
        css: css`
            margin-right: 16px;
            &:focus {
                outline: none;
            }
        `,
    },
    tableViewClickable: {
        css: css`
            &:focus {
                outline: none;
            }
        `,
    },
    tableViewIcon: {
        src: TableView,
        size: 24,
    },
    gridViewIcon: {
        src: GridView,
        size: 24,
    },
};

const styles = viewSelectStyles;

class ReorderPadViewSelect extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const view = getCookie("ReorderPad-View");
        if (view && this.props.view !== view) {
            this.props.setReorderPadView({ view: view as ReorderPadProductListViewType });
        }
    }

    render() {
        const { loaded, view, setReorderPadView } = this.props;

        if (!loaded) {
            return null;
        }

        const clickGridViewHandler = () => {
            changeView("Grid");
        };

        const clickTableViewHandler = () => {
            changeView("Table");
        };

        const changeView = (view: ReorderPadProductListViewType) => {
            setCookie("ReorderPad-View", view);
            setReorderPadView({ view });
        };

        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.title}>{translate("View")}</Typography>
                <Clickable
                    data-test-selector="viewSelectGrid"
                    {...styles.gridViewClickable}
                    onClick={clickGridViewHandler}
                >
                    <VisuallyHidden>{translate("Product Grid View")}</VisuallyHidden>
                    <Icon
                        {...styles.gridViewIcon}
                        color={view === "Grid" ? "common.ui18" : getSystemColor("common.ui19", 0.33)}
                    />
                </Clickable>
                <Clickable
                    data-test-selector="viewSelectList"
                    {...styles.tableViewClickable}
                    onClick={clickTableViewHandler}
                >
                    <VisuallyHidden>{translate("Product Table View")}</VisuallyHidden>
                    <Icon
                        {...styles.tableViewIcon}
                        color={view === "Table" ? "common.ui18" : getSystemColor("common.ui19", 0.33)}
                    />
                </Clickable>
            </StyledWrapper>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ReorderPadViewSelect);
